import { MAIN_BRANCH_ID } from '@torim-app/types';
import { getNumericHour, getTwoChars } from '@torim-app/utils';
import { useAdminAvailabilityStore, useAdminStore } from '@app/stores';

// Soft avatar palette (bg/fg), picked deterministically per member so colors stay stable.
const PALETTE: Array<{ bg: string; fg: string }> = [
  { bg: '#edeafd', fg: '#6831d0' },
  { bg: '#fde7f3', fg: '#be185d' },
  { bg: '#dbeafe', fg: '#1d4ed8' },
  { bg: '#fef9c3', fg: '#a16207' },
  { bg: '#dcfce7', fg: '#15803d' },
  { bg: '#ffe4e6', fg: '#be123c' },
  { bg: '#e0e7ff', fg: '#4338ca' },
];

export const initials = (fullName?: string): string => getTwoChars(fullName ?? '');

export const avatarStyle = (m: { userId?: string; fullName?: string }): { background: string; color: string } => {
  const key = m.userId || m.fullName || '';
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  const c = PALETTE[hash % PALETTE.length];
  return { background: c.bg, color: c.fg };
};

// Distinct branch ids a provider works at (untagged blocks = main branch).
export const teamMemberBranchIds = (userId: string): string[] =>
  useAdminAvailabilityStore().getProviderBranchIds(userId);

const toHourString = (h: unknown): string => (typeof h === 'string' ? h : (h as { name: string })?.name);

// Weekly working hours for a provider, optionally scoped to a single branch.
export const weeklyHours = (userId: string, branchId?: string): number => {
  const availability = useAdminAvailabilityStore().getAvailability?.[userId];
  if (!availability) return 0;
  let total = 0;
  for (const blocks of Object.values(availability)) {
    for (const b of blocks ?? []) {
      if (branchId && (b.branchId || MAIN_BRANCH_ID) !== branchId) continue;
      const from = getNumericHour(toHourString(b.from));
      const to = getNumericHour(toHourString(b.to));
      if (to > from) total += to - from;
    }
  }
  return Math.round(total);
};

// Regular (bookable) services this provider is attached to.
export const servicesForProvider = (userId: string) =>
  useAdminStore().getRegularServices.filter(s => (s.providingClients ?? []).includes(userId));

export const servicesCount = (userId: string): number => servicesForProvider(userId).length;
